<template>
  <footer class="footer-section">
    <div class="footer-wrapper">

      <!-- Top accent bar -->
      <div class="footer-accent-bar" />

      <!-- Main Footer Content -->
      <div class="footer-content">

        <!-- Column 1: Brand & Contact -->
        <Motion
          :initial="{ opacity: 0, y: 30 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :inViewOptions="{ once: true, margin: '-60px' }"
          :transition="{ duration: 0.6, ease: 'easeOut' }"
        >
        <div class="footer-col brand-col">
          <div class="footer-brand">
            <div class="footer-logo-wrap">
              <img src="/eduversity_logo.svg" alt="Qspiders Eduversity" class="footer-logo" />
            </div>
            <p class="footer-tagline">Industry-Integrated Learning for Future Engineers</p>
          </div>

          <div class="footer-contact">
            <div class="contact-item">
              <span class="contact-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <span class="contact-text">No. 01, Hayavadana Rao Rd, Basappa Layout, Gavipuram Extension, Kempegowda
                Nagar, Bengaluru, Karnataka 560004</span>
            </div>
            <div class="contact-item">
              <span class="contact-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <a href="mailto:eduversity@qspiders.com" class="contact-link">eduversity@qspiders.com</a>
            </div>
            <div class="contact-item">
              <span class="contact-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
              <div class="contact-phones">
                <a href="tel:7204851909" class="contact-link">7204851909</a>
                <span class="phone-sep">/</span>
                <a href="tel:7338667465" class="contact-link">7338667465</a>
              </div>
            </div>
          </div>
        </div>
        </Motion>

        <!-- Column 2: Quick Links -->
        <Motion
          :initial="{ opacity: 0, y: 30 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :inViewOptions="{ once: true, margin: '-60px' }"
          :transition="{ duration: 0.6, delay: 0.1, ease: 'easeOut' }"
        >
        <div class="footer-col links-col">
          <h4 class="footer-col-title">Quick Links</h4>
          <nav class="footer-links">
            <NuxtLink to="/hire-from-us" class="footer-link">Hire From Us</NuxtLink>
            <NuxtLink to="/placements" class="footer-link">Placements</NuxtLink>
            <NuxtLink to="/downloads" class="footer-link">Downloads</NuxtLink>
            <NuxtLink to="/contact-us" class="footer-link">Contact Us</NuxtLink>
          </nav>
        </div>
        </Motion>

        <!-- Column 3: Social & Programs -->
        <Motion
          :initial="{ opacity: 0, y: 30 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :inViewOptions="{ once: true, margin: '-60px' }"
          :transition="{ duration: 0.6, delay: 0.2, ease: 'easeOut' }"
        >
        <div class="footer-col social-col">
          <h4 class="footer-col-title">Connect With Us</h4>
          <div class="social-links">
            <a href="https://www.facebook.com/profile.php?id=61585843404752" target="_blank" rel="noopener noreferrer"
              class="social-link" aria-label="Facebook">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/qspiders-eduversity/" target="_blank" rel="noopener noreferrer"
              class="social-link" aria-label="LinkedIn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </a>
            <a href="https://www.instagram.com/qspiders_eduversity" target="_blank" rel="noopener noreferrer"
              class="social-link" aria-label="Instagram">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a href="https://whatsapp.com/channel/0029Va9T5QE7j6g9YwNJsE0Y" target="_blank" rel="noopener noreferrer"
              class="social-link" aria-label="WhatsApp">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </a>
          </div>

          <h4 class="footer-col-title programs-title">Programs</h4>
          <nav class="footer-links">
            <NuxtLink to="/btech" class="footer-link">B.Tech</NuxtLink>
            <NuxtLink to="/mtech" class="footer-link">M.Tech</NuxtLink>
            <NuxtLink to="/bca" class="footer-link">BCA</NuxtLink>
            <NuxtLink to="/mca" class="footer-link">MCA</NuxtLink>
            <NuxtLink to="/mba" class="footer-link">MBA</NuxtLink>
          </nav>
        </div>
        </Motion>

      </div>

      <!-- Bottom Bar -->
      <Motion
        :initial="{ opacity: 0 }"
        :whileInView="{ opacity: 1 }"
        :inViewOptions="{ once: true, margin: '-40px' }"
        :transition="{ duration: 0.5, ease: 'easeOut' }"
      >
      <div class="footer-bottom">
        <div class="footer-bottom-content">
          <p class="copyright">© 2025 Qspiders Eduversity. All Rights Reserved.</p>
          <div class="footer-bottom-links">
            <a href="#" class="bottom-link">Privacy Policy</a>
            <span class="bottom-sep">·</span>
            <a href="#" class="bottom-link">Terms of Service</a>
          </div>
        </div>
      </div>
      </Motion>

    </div>
  </footer>
</template>

<style scoped>
/* ── Section ─────────────────────────────────────────────────────────────── */
.footer-section {
  position: relative;
  z-index: 10;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

.footer-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Accent bar ──────────────────────────────────────────────────────────── */
.footer-accent-bar {
  height: 3px;
  background: linear-gradient(90deg, #ff7800, #ffbb3f);
}

/* ── Main Content ─────────────────────────────────────────────────────────── */
.footer-content {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 3rem;
  padding: 4rem 2rem 3rem;
}

/* ── Columns ─────────────────────────────────────────────────────────────── */
.footer-col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.footer-col-title {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin: 0;
}

/* ── Brand Column ──────────────────────────────────────────────────────────── */
.footer-brand {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.footer-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 200px;
  height: 66px;
  border-radius: 14px;
  background: #0a0a0a0f;
  /* border: 1px solid rgba(255, 120, 0, 0.3); */
  padding: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.footer-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.footer-tagline {
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  max-width: 240px;
}

/* ── Contact ──────────────────────────────────────────────────────────────── */
.footer-contact {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.contact-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 120, 0, 0.1);
  border: 1px solid rgba(255, 120, 0, 0.2);
  color: #ff7800;
}

.contact-text {
  font-size: 0.82rem;
  color: var(--text-secondary);
  line-height: 1.6;
  padding-top: 0.4rem;
}

.contact-link {
  font-size: 0.82rem;
  color: var(--text-secondary);
  text-decoration: none;
  padding-top: 0.4rem;
  transition: color 0.2s ease;
}

.contact-link:hover {
  color: #ff7800;
}

.contact-phones {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding-top: 0.4rem;
}

.phone-sep {
  color: var(--text-muted);
  font-size: 0.82rem;
}

/* ── Links ─────────────────────────────────────────────────────────────────── */
.footer-links {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.footer-link {
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.footer-link:hover {
  color: #ff7800;
  transform: translateX(4px);
}

.footer-link::before {
  content: '';
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 4px solid rgba(255, 120, 0, 0.5);
  transition: border-left-color 0.2s ease;
}

.footer-link:hover::before {
  border-left-color: #ff7800;
}

/* ── Social ───────────────────────────────────────────────────────────────── */
.social-links {
  display: flex;
  gap: 0.75rem;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--text-secondary);
  transition: all 0.3s ease;
}

.social-link:hover {
  background: rgba(255, 120, 0, 0.15);
  border-color: rgba(255, 120, 0, 0.4);
  color: #ff7800;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(255, 120, 0, 0.15);
}

.programs-title {
  margin-top: 0.5rem;
}

/* ── Bottom Bar ─────────────────────────────────────────────────────────────── */
.footer-bottom {
  border-top: 1px solid var(--border-color);
  padding: 1.5rem 2rem;
}

.footer-bottom-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.copyright {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin: 0;
}

.footer-bottom-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bottom-link {
  font-size: 0.78rem;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.2s ease;
}

.bottom-link:hover {
  color: #ff7800;
}

.bottom-sep {
  color: var(--text-muted);
  font-size: 0.78rem;
}

/* ── Responsive ─────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  .brand-col {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 3rem 1.5rem 2rem;
  }

  .footer-bottom-content {
    flex-direction: column;
    text-align: center;
  }

  .footer-logo {
    height: 40px;
  }
}
</style>
